import React, { useEffect, useMemo, useState } from 'react';
import { useSelector, useDispatch } from 'react-redux';
import { useSnackbar } from 'notistack';
import { Link as RouterLink } from 'react-router-dom';
import { Link } from '@mui/material';
import { useGridApiRef, GridActionsCellItem } from '@mui/x-data-grid';
import CancelIcon from '@mui/icons-material/Cancel';
import { useFormik } from 'formik';
import * as yup from 'yup';
import dayjs from 'dayjs';
import BreadcrumbsContainer from '../common/breadcurmbs/BreadcrumbsContainer';
import TaxbillList from '../../components/paymentmng/TaxbillList';
import { TaxbillListBox } from '../../components/paymentmng/TaxbillListBox';
import TaxbillStatusList from '../../components/paymentmng/TaxbillStatusList';
import AskDialog from '../../components/common/dialog/AskDialog';
import { listTaxbills } from '../../modules/payModule/taxbills';
import * as taxbillApi from '../../lib/api/taxbill';
import { getDefaultSearchRange } from '../../utils/defaultSearchRange';

const PaymentListContainer = () => {
  const { enqueueSnackbar } = useSnackbar();
  const dispatch = useDispatch();
  // 검색 시작일/종료일을 비워두지 않고 오늘 기준 앞뒤 1년을 기본 조회범위로 둔다.
  const defaultRange = getDefaultSearchRange();
  const [searchBillTyp, setsearchBillTyp] = useState('');
  const [searchTyp, setSearchTyp] = useState(false);
  const [searchKeyword, setSearchKeyword] = useState('invNo');
  const [searchWords, setSearchWords] = useState('');
  const [searchYn, setSearchYn] = useState('');
  const [searchStrtDt, setSearchStrtDt] = useState(defaultRange.searchStrtDt);
  const [searchEndDt, setSearchEndDt] = useState(defaultRange.searchEndDt);

  const { taxbills, error, loading } = useSelector(
    ({ payModule, loading }) => ({
      taxbills: payModule.taxbills.taxbills,
      error: payModule.taxbills.error,
      loading: loading['pay/LIST_TAXBILLS'],
    }),
  );

  const searchList = (values) => {
    const startDate =
      values.searchStrtDt !== null && values.searchStrtDt !== ''
        ? dayjs(values.searchStrtDt).format('YYYY-MM-DD')
        : '';
    const endDate =
      values.searchEndDt !== null && values.searchEndDt !== ''
        ? dayjs(values.searchEndDt).format('YYYY-MM-DD')
        : '';

    let searchData = {
      billTyp: searchBillTyp,
      searchTyp: searchTyp,
      reqCompYn: searchYn,
      strtDt: startDate,
      endDt: endDate,
      [values.searchKeyword]: values.searchWords,
    };

    setSearchKeyword(values.searchKeyword);
    setSearchWords(values.searchWords);
    setSearchStrtDt(values.searchStrtDt);
    setSearchEndDt(values.searchEndDt);

    dispatch(listTaxbills(searchData));
  };

  // 행별 취소 (발급 취소 / 수금·지급 완료 취소)
  const [cancelTarget, setCancelTarget] = useState(null); // { id, type: 'issue' | 'proc' }

  const requestCancel = (id, type) => (event) => {
    event.stopPropagation();
    setCancelTarget({ id, type });
  };

  const onCancelClose = () => setCancelTarget(null);

  const onCancelConfirm = async () => {
    if (!cancelTarget) return;
    const { id, type } = cancelTarget;
    try {
      const body =
        type === 'proc'
          ? { invUuid: id, procCompYn: 'N' }
          : { invUuid: id, reqCompYn: 'N' };
      const res = await taxbillApi.editTaxbill(body);
      if (res.data) {
        enqueueSnackbar(
          type === 'proc' ? '수금/지급 완료 취소' : '발급 취소 완료',
          { variant: 'success' },
        );
        searchList(formik.values);
      }
    } catch (err) {
      const message =
        typeof err.response !== 'undefined'
          ? err.response.data.message
          : err.message;
      enqueueSnackbar(message, { variant: 'error' });
    }
    setCancelTarget(null);
  };

  const apiRef = useGridApiRef();

  const columns = useMemo(
    () => [
      { field: 'id', hide: true, sortable: false, editable: false },
      { field: 'invUuid', hide: true, sortable: false, editable: false }, //세금계산서ID
      { field: 'roUuid', hide: true, sortable: false, editable: false }, //수주ID
      { field: 'billUuid', hide: true, sortable: false, editable: false }, //거래ID
      { field: 'prjUuid', hide: true, sortable: false, editable: false }, //프로젝트ID
      {
        field: 'invYn',
        hide: true,
        editable: false,
      },

      {
        field: 'invNo',
        headerName: '계산서번호',
        minWidth: 200,
        flex: 1,
        type: 'string',
        editable: false,
        align: 'left',
        headerAlign: 'center',
        renderCell: (params) => (
          <Link
            underline="none"
            component={RouterLink}
            to={`/paymentmng/${params.row.invUuid}`}
          >
            {params.value}
          </Link>
        ),
      },
      {
        field: 'partnerNm',
        headerName: '고객명',
        minWidth: 200,
        flex: 1,
        type: 'string',
        editable: false,
        align: 'left',
        headerAlign: 'center',
      },
      {
        field: 'cstNm',
        headerName: '사업명',
        minWidth: 200,
        flex: 1,
        type: 'string',
        editable: false,
        align: 'left',
        headerAlign: 'center',
      },
      {
        field: 'billTyp',
        headerName: '유형',
        minWidth: 100,
        flex: 1,
        type: 'string',
        editable: false,
        align: 'center',
        headerAlign: 'center',
      },
      {
        field: 'reqPlanUsrHq',
        headerName: '본부',
        description: '',
        type: 'string',
        flex: 0.8,
        minWidth: 120,
      },
      {
        field: 'reqPlanUsrDpt',
        headerName: '부서',
        description: '',
        type: 'string',
        flex: 0.8,
        minWidth: 120,
      },
      {
        field: 'reqPlanUsrNm',
        headerName: '요청자',
        description: '',
        type: 'string',
        flex: 0.8,
        minWidth: 120,
      },
      {
        field: 'procCompDt',
        headerName: '수금예정일자',
        description: '',
        align: 'center',
        headerAlign: 'center',
        type: 'date',
        flex: 0.8,
        minWidth: 150,
      },
      {
        field: 'invAmt',
        headerName: '계산서 금액',
        minWidth: 150,
        flex: 1,
        type: 'number',
      },
      {
        field: 'procCompYn',
        headerName: '수금/지급 완료',
        description: '',
        align: 'center',
        headerAlign: 'center',
        flex: 0.8,
        minWidth: 120,
      },
      {
        field: 'procCompUsrNm',
        headerName: '처리자',
        description: '처리자',
        type: 'string',
        flex: 0.8,
        minWidth: 100,
      },
      {
        field: 'actions',
        type: 'actions',
        headerName: '취소',
        width: 110,
        getActions: (params) => {
          const row = params.row;
          if (row.procCompYn === 'Y') {
            return [
              <GridActionsCellItem
                icon={<CancelIcon />}
                label="수금취소"
                onClick={requestCancel(row.invUuid, 'proc')}
                color="error"
              />,
            ];
          }
          if (row.reqCompYn === 'Y') {
            return [
              <GridActionsCellItem
                icon={<CancelIcon />}
                label="발급취소"
                onClick={requestCancel(row.invUuid, 'issue')}
                color="warning"
              />,
            ];
          }
          return [];
        },
      },
    ],
    [],
  );

  //검색 관련 사항
  const initialValues = {
    searchStrtDt: searchStrtDt,
    searchEndDt: searchEndDt,
    searchKeyword: searchKeyword,
    searchWords: searchWords,
  };

  const validationSchema = yup.object({
    searchKeyword: yup.string('').nullable(),
    searchWords: yup.string('').trim().nullable(),
    searchStrtDt: yup.date().nullable().default(undefined),
    searchEndDt: yup
      .date()
      .min(yup.ref('searchStrtDt'), '등록 종료일을 확인하세요')
      .when('searchStrtDt', (searchStrtDt, schema) => {
        if (searchStrtDt) return schema.required('등록 종료일을 입력하세요');
      })
      .nullable()
      .default(undefined),
  });

  const formik = useFormik({
    initialValues: initialValues,
    enableReinitialize: true,
    validationSchema: validationSchema,
    onSubmit: (values, { setSubmitting }) => {
      setSubmitting(true);

      setTimeout(() => {
        searchList(values);
        setSubmitting(false);
      }, 1000);
    },
  });

  useEffect(() => {
    searchList(formik.values);
  }, [searchBillTyp, searchTyp, searchYn]);

  const onChangeBillTyp = (billTyp) => {
    if (billTyp === searchBillTyp) {
      billTyp = '';
    }

    setsearchBillTyp(billTyp);
  };

  const onChangeYn = (useYn) => {
    if (useYn === searchBillTyp) {
      useYn = '';
    }

    setSearchYn(useYn);
  };

  const toggleSearchTyp = () => {
    setSearchTyp(!searchTyp);
  };

  return (
    <>
      <BreadcrumbsContainer
        title="세금계산서 관리/수금관리/계산서 리스트"
        icon="pay"
      />
      <TaxbillListBox formik={formik} />
      <TaxbillStatusList
        searchBillTyp={searchBillTyp}
        onChangeBillTyp={onChangeBillTyp}
        searchYn={searchYn}
        onChangeYn={onChangeYn}
        searchTyp={searchTyp}
        toggleSearchTyp={toggleSearchTyp}
      />
      <TaxbillList
        columns={columns}
        rows={taxbills}
        error={error}
        loading={loading}
      />
      <AskDialog
        visible={!!cancelTarget}
        title={cancelTarget?.type === 'proc' ? '수금/지급 완료 취소' : '발급 취소'}
        description={
          cancelTarget?.type === 'proc'
            ? '수금/지급 완료를 취소합니다. 완료일자·처리자·실적확정이 초기화됩니다.\n계속하시겠습니까?'
            : '세금계산서 발급을 취소합니다. 발급일과 이후 수금/지급·실적 단계가 모두 초기화됩니다.\n계속하시겠습니까?'
        }
        confirmText="취소 처리"
        cancelText="닫기"
        onConfirm={onCancelConfirm}
        onCancle={onCancelClose}
      />
    </>
  );
};

export default PaymentListContainer;
